import {
  IDepot, IBac, IPlanningEntry, ContentType, IRestriction,
  IClient, IDepotGeo, ICapacite, IClientSynthese,
  IBacAction, IBacActionCreatePayload,
} from '../../models';
import { IDataProvider } from './IDataProvider';
import { IProductsConfig } from '../../config/productsConfig';

export class RestDataProvider implements IDataProvider {
  constructor(private baseUrl: string = '/api') {}

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const res = await fetch(`${this.baseUrl}${path}`, {
      method,
      credentials: 'include',
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`${method} ${path} -> ${res.status} ${text}`);
    }
    if (res.status === 204) return undefined as unknown as T;
    return res.json() as Promise<T>;
  }

  // Depots
  getDepots(): Promise<IDepot[]> { return this.request('GET', '/depots'); }
  addDepot(title: string): Promise<IDepot> { return this.request('POST', '/depots', { title }); }
  updateDepot(id: number, title: string): Promise<void> {
    return this.request('PUT', `/depots/${id}`, { title });
  }
  deleteDepot(id: number): Promise<void> { return this.request('DELETE', `/depots/${id}`); }

  // Bacs
  getBacs(): Promise<IBac[]> { return this.request('GET', '/bacs'); }
  getBacsByDepot(depotId: number): Promise<IBac[]> { return this.request('GET', `/bacs?depotId=${depotId}`); }
  async addBac(bac: Omit<IBac, 'Id' | 'DepotTitle'>): Promise<number> {
    const created = await this.request<IBac>('POST', '/bacs', bac);
    return created.Id;
  }
  updateBac(id: number, updates: Partial<IBac>): Promise<void> {
    return this.request('PUT', `/bacs/${id}`, updates);
  }
  deleteBac(id: number): Promise<void> { return this.request('DELETE', `/bacs/${id}`); }

  // Planning
  getPlanningEntries(bacIds?: number[]): Promise<IPlanningEntry[]> {
    const q = bacIds && bacIds.length ? `?bacIds=${bacIds.join(',')}` : '';
    return this.request('GET', `/planning${q}`);
  }
  upsertPlanningEntry(bacId: number, mois: string, contenu: string, typeContenu: ContentType): Promise<void> {
    return this.request('POST', '/planning', { bacId, mois, contenu, typeContenu });
  }
  deletePlanningEntry(id: number): Promise<void> { return this.request('DELETE', `/planning/${id}`); }

  // Restrictions
  getRestrictions(): Promise<IRestriction[]> { return this.request('GET', '/restrictions'); }
  async addRestriction(r: Omit<IRestriction, 'Id' | 'DepotTitle'>): Promise<number> {
    const created = await this.request<IRestriction>('POST', '/restrictions', r);
    return created.Id;
  }
  updateRestriction(id: number, updates: Partial<IRestriction>): Promise<void> {
    return this.request('PUT', `/restrictions/${id}`, updates);
  }
  deleteRestriction(id: number): Promise<void> { return this.request('DELETE', `/restrictions/${id}`); }

  // Clients dashboard
  getClients(): Promise<IClient[]> { return this.request('GET', '/clients'); }
  getDepotsGeo(): Promise<IDepotGeo[]> { return this.request('GET', '/depots-geo'); }
  getCapacites(clientId?: number): Promise<ICapacite[]> {
    const q = clientId ? `?clientId=${clientId}` : '';
    return this.request('GET', `/capacites${q}`);
  }
  getClientSynthese(): Promise<IClientSynthese[]> { return this.request('GET', '/client-synthese'); }

  // Products config
  getProductsConfig(): Promise<IProductsConfig> { return this.request('GET', '/products-config'); }
  saveProductsConfig(config: IProductsConfig): Promise<void> {
    return this.request('PUT', '/products-config', config);
  }

  // Bac actions
  getBacActions(filter?: { bacId?: number; bacIds?: number[]; type?: string }): Promise<IBacAction[]> {
    const params: string[] = [];
    if (filter?.bacId !== undefined) params.push(`bacId=${filter.bacId}`);
    if (filter?.bacIds && filter.bacIds.length > 0) params.push(`bacIds=${filter.bacIds.join(',')}`);
    if (filter?.type) params.push(`type=${encodeURIComponent(filter.type)}`);
    const q = params.length ? `?${params.join('&')}` : '';
    return this.request('GET', `/bac-actions${q}`);
  }

  async createBacAction(payload: IBacActionCreatePayload, file?: File): Promise<IBacAction> {
    const form = new FormData();
    form.append('bacId', String(payload.bacId));
    form.append('type', payload.type);
    form.append('status', payload.status);
    form.append('comment', payload.comment);
    form.append('doneAt', payload.doneAt);
    if (payload.planningEntryId) form.append('planningEntryId', String(payload.planningEntryId));
    if (payload.newInterventionMonth) form.append('newInterventionMonth', payload.newInterventionMonth);
    if (file) form.append('attachment', file, file.name);

    const res = await fetch(`${this.baseUrl}/bac-actions`, {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`POST /bac-actions -> ${res.status} ${text}`);
    }
    return res.json() as Promise<IBacAction>;
  }

  deleteBacAction(id: number): Promise<void> {
    return this.request('DELETE', `/bac-actions/${id}`);
  }

  getBacActionAttachmentUrl(id: number): string {
    return `${this.baseUrl}/bac-actions/${id}/attachment`;
  }
}
